/* eslint-disable no-use-before-define */
import type { MultilingualText } from '@/utils/localizeString'

// Re-exported so existing importers keep working; the type lives next to its resolver.
export type { MultilingualText }

export enum ChecklistTemplateType {
  CHECKLIST = 'checklist',
  FORM = 'form'
}

export enum CustomQuestionType {
  TEXT = 'text',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DROPDOWN = 'dropdown'
}

export enum ComparisonOperator {
  EQUAL = '=',
  LESS_THAN = '<',
  GREATER_THAN = '>'
}

export interface BooleanValidation {
  expectedValue: boolean
}

export interface NumberValidation {
  operator: ComparisonOperator
  value: number
}

export interface DropdownValidation {
  // Index into BaseCustomQuestion.options. An index, not the text, because the option is
  // multilingual — there is no single string to compare against.
  expectedOption: number
}

export type ValidationRule = BooleanValidation | NumberValidation | DropdownValidation

export interface BaseCustomQuestion {
  type: CustomQuestionType
  // `| string` like every other multilingual field: rows written before this was multilingual hold
  // a plain string and are never migrated. Always read via getLocalizedString().
  question: MultilingualText | string
  unit?: MultilingualText | string
  // DROPDOWN only: the answer options, one MultilingualText each. The answer stores the index.
  options?: MultilingualText[]
  value?: string | number | boolean
  images?: string[]
}

export interface CustomQuestionWithValidation extends BaseCustomQuestion {
  checkValue: true
  validationValue: ValidationRule
  strictlyRequired?: boolean
}

export interface CustomQuestionWithoutValidation extends BaseCustomQuestion {
  checkValue: false
  validationValue?: never
  strictlyRequired?: never
}

export type CustomQuestion = CustomQuestionWithValidation | CustomQuestionWithoutValidation

export interface ObjectDataField {
  fieldName: string
  displayName: MultilingualText | string // `| string` = legacy rows, see BaseCustomQuestion.question
  required: boolean
  value?: string
}

export interface ChecklistObjectData {
  fields: ObjectDataField[]
}

export interface IChecklistTemplate {
  _id?: string
  name: MultilingualText | string
  type: ChecklistTemplateType
  company?: string
  companyObject?: any
  isGlobal?: boolean
  requiresVehicle: boolean
  /** Catalog vehicle ids this template applies to. Empty/absent = applies to all vehicles. */
  vehicles?: string[]
  /**
   * AssetType id a vehicle inspection has to be carried out WITH — the tool, not the subject.
   * If set, the report must pick an asset of that type. Absent = no object needed.
   */
  requiredAssetType?: string
  requiresAsset?: boolean
  /**
   * Months an OK run of this checklist keeps the selected object calibrated — finishing one moves
   * its `calibrationDueDate` forward. Only set together with `requiresAsset`; unset = no calibration.
   */
  calibrationValidityMonths?: number
  objectDataConfig?: ChecklistObjectData
  customQuestions: CustomQuestion[]
  active: boolean
  version: number
  createdBy?: string
  createdByObject?: any
  createdAt?: Date
  updatedAt?: Date
}

export class ChecklistTemplate implements IChecklistTemplate {
  constructor (
    public name: MultilingualText = { de: '', en: '', it: '' },
    public type = ChecklistTemplateType.CHECKLIST,
    public requiresVehicle = false,
    public requiresAsset = false,
    public customQuestions: CustomQuestion[] = [],
    public active = true,
    public version = 1,
    public _id?: string,
    public company?: string,
    public companyObject?: any,
    public isGlobal = false,
    public objectDataConfig?: ChecklistObjectData,
    public createdBy?: string,
    public createdByObject?: any,
    public createdAt?: Date,
    public updatedAt?: Date,
    public vehicles: string[] = [],
    public requiredAssetType?: string,
    public calibrationValidityMonths?: number,
  ) {
    return this
  }
}